import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle, PencilSimple, ArrowsSplit, UserMinus, Receipt, PaperPlaneTilt, CaretDown, CircleNotch } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };

export function ReviewQueue({ businessId }) {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [changeFor, setChangeFor] = useState(null);
  const [changeCat, setChangeCat] = useState("");
  const [splitFor, setSplitFor] = useState(null);
  const [splitBiz, setSplitBiz] = useState("");
  const [loadError, setLoadError] = useState(false);

  const load = () => { setLoadError(false); return api.get(`/businesses/${businessId}/review-queue`).then((r) => setData(r.data)).catch(() => setLoadError(true)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const act = async (item, action, extra = {}) => {
    setBusyId(item.transaction_id);
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/review-queue/${item.transaction_id}/resolve`, { action, ...extra });
      toast.success(res.rowan_message);
      setChangeFor(null); setSplitFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't do that."); }
    finally { setBusyId(null); }
  };

  const finalizePlaid = async () => {
    setBusyId("finalize");
    try {
      const { data: res } = await api.post(`/businesses/${businessId}/plaid/review-session/${data.plaid_session_id}/finalize`);
      toast.success(res.rowan_message);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't finish the import."); }
    finally { setBusyId(null); }
  };

  const doSplit = (item) => {
    const biz = parseFloat(splitBiz);
    const total = parseFloat(item.amount);
    if (!(biz > 0) || biz >= total) return toast.error(`Enter the business share (more than $0, less than $${total.toFixed(2)})`);
    act(item, "split", { splits: [
      { amount: Math.round(biz * 100) / 100, category: item.suggested_category || item.current_category || "", personal: false },
      { amount: Math.round((total - biz) * 100) / 100, personal: true },
    ]});
  };

  if (!data) {
    if (loadError) {
      return (
        <div className="bk-card p-6 flex flex-wrap items-center gap-3" data-testid="review-queue-error">
          <p className="text-sm flex-1" style={{ color: "var(--bk-text)" }}>Rowan couldn't load the review queue just now.</p>
          <button onClick={load} data-testid="review-queue-retry" className="bk-btn-primary px-4 py-2 text-sm font-semibold">Try again</button>
        </div>
      );
    }
    return (
      <div className="bk-card p-6 flex items-center gap-3" data-testid="review-queue-loading">
        <CircleNotch size={20} className="animate-spin" color="var(--bk-accent)" />
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Rowan is checking each transaction…</p>
          <p className="text-xs" style={label}>Suggesting a category for anything uncategorized. This can take a few seconds.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="review-queue">
      <p className="text-base font-semibold mb-5" style={{ color: "var(--bk-text)" }} data-testid="review-queue-headline">{data.rowan_headline}</p>
      {data.plaid_ready_to_finalize && (
        <div className="bk-card p-4 mb-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--bk-rowan-bg)" }} data-testid="plaid-finalize-banner">
          <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>Every bank-import item is reviewed — finish the import to add them to your books.</p>
          <button disabled={busyId === "finalize"} onClick={finalizePlaid} data-testid="plaid-finalize-btn"
            className="bk-btn-primary px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">Finish the bank import</button>
        </div>
      )}
      <div className="space-y-4">
        {data.items.map((item) => {
          const pool = item.type === "income" ? data.categories.income : data.categories.expense;
          const busy = busyId === item.transaction_id;
          const isPlaid = item.source === "plaid";
          return (
            <div key={item.transaction_id} className="bk-card p-4" data-testid={`review-item-${item.transaction_id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="text-sm font-bold" style={{ color: "var(--bk-text)" }}>{item.description}</p>
                  <p className="text-xs mt-0.5" style={label}>{item.date} · {item.type === "income" ? "money in" : "money out"} · ${Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}{item.sent_to_bookkeeper ? " · with your bookkeeper" : ""}{isPlaid ? " · from your bank feed" : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {isPlaid && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`review-plaid-pill-${item.transaction_id}`}>
                      bank import{item.flag_type ? ` · ${String(item.flag_type).replace("_", " ")}` : ""}
                    </span>
                  )}
                  {item.suggested_category && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
                      Rowan suggests: {item.suggested_category}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm mt-2 italic leading-relaxed" style={label} data-testid={`review-reason-${item.transaction_id}`}>Rowan: “{item.rowan_reason}”</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button disabled={busy} onClick={() => act(item, "accept")} data-testid={`review-accept-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-50" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>
                  <CheckCircle size={14} weight="fill" /> Accept
                </button>
                <button disabled={busy} onClick={() => { setChangeFor(changeFor === item.transaction_id ? null : item.transaction_id); setChangeCat(""); }} data-testid={`review-change-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <PencilSimple size={14} weight="bold" /> Change
                </button>
                {isPlaid ? (
                  <button disabled={busy} onClick={() => act(item, "mark_transfer")} data-testid={`review-transfer-${item.transaction_id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                    <ArrowsSplit size={14} weight="bold" /> It's a transfer
                  </button>
                ) : (
                  <>
                <button disabled={busy} onClick={() => { setSplitFor(splitFor === item.transaction_id ? null : item.transaction_id); setSplitBiz(""); }} data-testid={`review-split-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <ArrowsSplit size={14} weight="bold" /> Split
                </button>
                <button disabled={busy} onClick={() => act(item, "mark_personal")} data-testid={`review-personal-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}>
                  <UserMinus size={14} weight="bold" /> Mark personal
                </button>
                <button disabled={busy} onClick={() => act(item, "attach_receipt", { note: "Receipt confirmed by owner" })} data-testid={`review-receipt-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  <Receipt size={14} weight="bold" /> {item.has_receipt ? "Receipt ✓" : "Attach receipt"}
                </button>
                <button disabled={busy || item.sent_to_bookkeeper} onClick={() => act(item, "send_to_bookkeeper", { note: "Owner asked for a second look" })} data-testid={`review-bookkeeper-${item.transaction_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-50" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                  <PaperPlaneTilt size={14} weight="bold" /> {item.sent_to_bookkeeper ? "With bookkeeper" : "Send to bookkeeper"}
                </button>
                  </>
                )}
              </div>
              {changeFor === item.transaction_id && (
                <div className="flex items-center gap-2 mt-3">
                  <div className="relative">
                    <select value={changeCat} onChange={(e) => setChangeCat(e.target.value)} data-testid={`review-change-select-${item.transaction_id}`}
                      className="appearance-none px-3 py-2 pr-8 rounded-md border bg-white text-sm" style={{ borderColor: "var(--bk-border)" }}>
                      <option value="">What was this really?</option>
                      {pool.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <CaretDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--bk-text-2)" />
                  </div>
                  <button disabled={!changeCat || busy} onClick={() => act(item, "change", { category: changeCat })} data-testid={`review-change-save-${item.transaction_id}`}
                    className="bk-btn-primary px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">Save</button>
                </div>
              )}
              {splitFor === item.transaction_id && (
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-xs" style={label}>Business share $</span>
                  <input value={splitBiz} onChange={(e) => setSplitBiz(e.target.value)} type="number" step="0.01" data-testid={`review-split-input-${item.transaction_id}`}
                    className="w-28 px-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--bk-border)" }} placeholder="0.00" />
                  <span className="text-xs" style={label}>— the rest stays personal</span>
                  <button disabled={busy} onClick={() => doSplit(item)} data-testid={`review-split-save-${item.transaction_id}`}
                    className="bk-btn-primary px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">Split it</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
